import { documentService } from './documentService';
import openai from './openaiClient';
import DocumentContentExtractor from './documentContentExtractor';

export const aiAnalysisService = {
  // Get document bases for selection
  async getDocumentBases() {
    try {
      return await documentService?.getDocumentBases();
    } catch (error) {
      console.error('Error getting document bases:', error);
      throw error;
    }
  },

  // Get real documents and generate analysis data for specific document base
  async getAnalysisData(documentBaseId = 'all') {
    try {
      let documents = [];
      
      if (documentBaseId === 'all') {
        // Fetch all documents from the database
        documents = await documentService?.getAllDocuments();
      } else {
        // Fetch documents from specific document base
        documents = await this.getDocumentsByDocumentBase(documentBaseId);
      }
      
      if (!documents || documents?.length === 0) {
        return {
          documentsAnalyzed: 0,
          categories: [
            { type: 'gap_detection', count: 0, resolved: 0, severity: 'high', icon: 'Search' },
            { type: 'inconsistencies', count: 0, resolved: 0, severity: 'medium', icon: 'AlertTriangle' },
            { type: 'contradictions', count: 0, resolved: 0, severity: 'critical', icon: 'XCircle' },
            { type: 'grammar_issues', count: 0, resolved: 0, severity: 'low', icon: 'Type' },
            { type: 'spelling_errors', count: 0, resolved: 0, severity: 'low', icon: 'Spellcheck' },
            { type: 'language_issues', count: 0, resolved: 0, severity: 'medium', icon: 'Globe' }
          ],
          allFindings: [],
          documentOptions: [{ value: 'all', label: 'All Documents' }]
        };
      }

      // Generate realistic analysis based on actual documents
      const analysisFindings = documents?.map((doc, index) => {
        const findingTypes = ['gap_detection', 'inconsistencies', 'contradictions', 'grammar_issues', 'spelling_errors', 'language_issues'];
        const severities = ['critical', 'high', 'medium', 'low'];
        const statuses = ['pending', 'in_review', 'resolved'];
        
        // Generate findings based on document type and name
        const randomType = findingTypes?.[index % findingTypes?.length];
        const randomSeverity = severities?.[Math.floor(Math.random() * severities?.length)];
        const randomStatus = statuses?.[Math.floor(Math.random() * statuses?.length)];
        
        return {
          id: index + 1,
          type: randomType,
          severity: randomSeverity,
          status: randomStatus,
          title: this.generateFindingTitle(doc, randomType),
          description: this.generateFindingDescription(doc, randomType),
          documentName: doc?.name,
          documentId: doc?.id,
          documentBaseId: doc?.documentBaseId,
          documentBaseName: doc?.documentBaseName,
          location: this.generateLocation(doc, randomType),
          detectedAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)?.toISOString(),
          affectedContent: this.generateAffectedContent(doc, randomType),
          suggestion: this.generateSuggestion(doc, randomType),
          suggestedContent: this.generateSuggestedContent(doc, randomType),
          additionalContext: this.generateAdditionalContext(doc, randomType)
        };
      });

      // Calculate category counts
      const categories = [
        { 
          type: 'gap_detection', 
          count: analysisFindings?.filter(f => f?.type === 'gap_detection')?.length,
          resolved: analysisFindings?.filter(f => f?.type === 'gap_detection' && f?.status === 'resolved')?.length,
          severity: 'high', 
          icon: 'Search' 
        },
        { 
          type: 'inconsistencies', 
          count: analysisFindings?.filter(f => f?.type === 'inconsistencies')?.length,
          resolved: analysisFindings?.filter(f => f?.type === 'inconsistencies' && f?.status === 'resolved')?.length,
          severity: 'medium', 
          icon: 'AlertTriangle' 
        },
        { 
          type: 'contradictions', 
          count: analysisFindings?.filter(f => f?.type === 'contradictions')?.length,
          resolved: analysisFindings?.filter(f => f?.type === 'contradictions' && f?.status === 'resolved')?.length,
          severity: 'critical', 
          icon: 'XCircle' 
        },
        { 
          type: 'grammar_issues', 
          count: analysisFindings?.filter(f => f?.type === 'grammar_issues')?.length,
          resolved: analysisFindings?.filter(f => f?.type === 'grammar_issues' && f?.status === 'resolved')?.length,
          severity: 'low', 
          icon: 'Type' 
        },
        { 
          type: 'spelling_errors', 
          count: analysisFindings?.filter(f => f?.type === 'spelling_errors')?.length,
          resolved: analysisFindings?.filter(f => f?.type === 'spelling_errors' && f?.status === 'resolved')?.length,
          severity: 'low', 
          icon: 'Spellcheck' 
        },
        { 
          type: 'language_issues', 
          count: analysisFindings?.filter(f => f?.type === 'language_issues')?.length,
          resolved: analysisFindings?.filter(f => f?.type === 'language_issues' && f?.status === 'resolved')?.length,
          severity: 'medium', 
          icon: 'Globe' 
        }
      ];

      // Create document options for filters
      const documentOptions = [
        { value: 'all', label: 'All Documents' },
        ...documents?.map(doc => ({
          value: doc?.id,
          label: doc?.name
        }))
      ];

      return {
        documentsAnalyzed: documents?.length,
        categories,
        allFindings: analysisFindings,
        documentOptions
      };
    } catch (error) {
      console.error('Error getting analysis data:', error);
      throw error;
    }
  },

  // Get documents from specific document base
  async getDocumentsByDocumentBase(documentBaseId) {
    try {
      const { supabase } = await import('../lib/supabase');
      
      const { data: documents, error } = await supabase?.from('documents')?.select(`
          id,
          name,
          description,
          file_path,
          file_size,
          mime_type,
          created_at,
          updated_at,
          owner_id,
          document_base_id,
          folder_id,
          profiles:owner_id (
            full_name,
            email
          ),
          document_bases:document_base_id (
            name
          )
        `)?.eq('document_base_id', documentBaseId)?.eq('status', 'active')?.order('updated_at', { ascending: false });

      if (error) throw error;

      return documents?.map(doc => ({
        id: doc?.id,
        name: doc?.name,
        description: doc?.description,
        filePath: doc?.file_path,
        size: doc?.file_size,
        mimeType: doc?.mime_type,
        type: documentService?.getFileTypeFromMime(doc?.mime_type),
        createdAt: doc?.created_at,
        updatedAt: doc?.updated_at,
        ownerId: doc?.owner_id,
        ownerName: doc?.profiles?.full_name || doc?.profiles?.email,
        documentBaseId: doc?.document_base_id,
        documentBaseName: doc?.document_bases?.name,
        folderId: doc?.folder_id
      })) || [];
    } catch (error) {
      console.error('Error fetching documents by document base:', error);
      throw new Error('Failed to fetch documents for document base');
    }
  },

  // ENHANCED: Get document content for AI analysis with proper content extraction
  async getDocumentContent(documentId) {
    try {
      const { supabase } = await import('../lib/supabase');
      
      const { data: document, error } = await supabase?.from('documents')?.select('*')?.eq('id', documentId)?.single();
      
      if (error || !document) {
        throw new Error('Document not found');
      }

      console.log(`Retrieving content for document: ${document?.name} (${document?.mime_type})`);

      // Get signed URL for document content
      const { data: signedUrlData, error: urlError } = await supabase?.storage?.from('documents')?.createSignedUrl(document?.file_path, 3600);
      
      if (urlError) {
        console.warn('Could not generate signed URL for document:', urlError);
        // Return metadata if signed URL fails
        return {
          id: document?.id,
          name: document?.name,
          content: `Document: ${document?.name}\nType: ${document?.mime_type}\nSize: ${document?.file_size} bytes\nDescription: ${document?.description || 'No description available'}\nNote: Unable to retrieve document content for analysis.`,
          mimeType: document?.mime_type,
          metadata: {
            hasContent: false,
            error: 'Signed URL generation failed'
          }
        };
      }

      // Fetch and extract document content
      try {
        console.log(`Downloading content from: ${signedUrlData?.signedUrl}`);
        const response = await fetch(signedUrlData?.signedUrl);
        
        if (!response?.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const fileBlob = await response?.blob();
        console.log(`Downloaded blob size: ${fileBlob?.size} bytes`);

        // Extract text content using our enhanced extractor
        const extractedContent = await DocumentContentExtractor?.extractPolicyContent(
          fileBlob, 
          document?.mime_type, 
          document?.name
        );

        return {
          id: document?.id,
          name: document?.name,
          content: extractedContent?.text,
          mimeType: document?.mime_type,
          metadata: {
            ...extractedContent?.metadata,
            hasContent: true,
            originalSize: document?.file_size
          }
        };
      } catch (fetchError) {
        console.warn(`Could not retrieve content for ${document?.name}:`, fetchError);
        
        // Return enhanced metadata if content retrieval fails
        return {
          id: document?.id,
          name: document?.name,
          content: `DOCUMENT METADATA ANALYSIS: ${document?.name}

TYPE: ${document?.mime_type} 
SIZE: ${document?.file_size} bytes
DESCRIPTION: ${document?.description || 'No description available'}

POLICY DOCUMENT CONTEXT:
This appears to be a policy or procedure document based on the filename "${document?.name}". 

LIKELY CONTENT AREAS (based on document name):
${this.generateContentPredictions(document?.name)}

ANALYSIS NOTE: Direct content extraction failed, but this document likely contains important policy information that should be reviewed manually for:
- Compliance requirements and schedules
- Access control procedures
- Review intervals (30, 60, 90, 120, 180 days)
- Security protocols and standards
- Implementation guidelines

ERROR: ${fetchError?.message || 'Content retrieval failed'}`,
          mimeType: document?.mime_type,
          metadata: {
            hasContent: false,
            error: fetchError?.message,
            predictedContent: true
          }
        };
      }
    } catch (error) {
      console.error('Error getting document content:', error);
      throw error;
    }
  },

  // Generate content predictions based on document name
  generateContentPredictions(documentName) {
    const name = documentName?.toLowerCase() || '';
    let predictions = [];

    if (name?.includes('access control') || name?.includes('access')) {
      predictions?.push('- User access management and provisioning procedures');
      predictions?.push('- Privileged access controls and reviews');
      predictions?.push('- Access certification and recertification schedules');
      predictions?.push('- Authentication and authorization requirements');
    }

    if (name?.includes('policy') || name?.includes('procedure')) {
      predictions?.push('- Policy scope and applicability');
      predictions?.push('- Compliance requirements and standards');
      predictions?.push('- Implementation guidelines and responsibilities');
      predictions?.push('- Review and update procedures');
    }

    if (name?.includes('information') || name?.includes('data')) {
      predictions?.push('- Data classification and handling procedures');
      predictions?.push('- Information security controls');
      predictions?.push('- Data retention and disposal requirements');
      predictions?.push('- Privacy and confidentiality measures');
    }

    if (name?.includes('security')) {
      predictions?.push('- Security control implementations');
      predictions?.push('- Incident response procedures');
      predictions?.push('- Risk management processes');
      predictions?.push('- Security awareness and training');
    }

    if (predictions?.length === 0) {
      predictions?.push('- General policy and procedure information');
      predictions?.push('- Organizational requirements and standards');
      predictions?.push('- Compliance and governance frameworks');
    }

    return predictions?.join('\n');
  },

  // Natural language query processing using OpenAI with ENHANCED DOCUMENT CONTENT ACCESS
  async processNaturalLanguageQuery(query, analysisData, documentBaseId = 'all') {
    try {
      if (!import.meta.env?.VITE_OPENAI_API_KEY) {
        return 'OpenAI API key is not configured. Please add your VITE_OPENAI_API_KEY to the environment variables to enable AI-powered document analysis.';
      }

      console.log('Starting enhanced document analysis query processing...');

      // Get relevant documents based on the selected document base
      let documents = [];
      if (documentBaseId === 'all') {
        documents = await documentService?.getAllDocuments();
      } else {
        documents = await this.getDocumentsByDocumentBase(documentBaseId);
      }

      // ENHANCED: Actually retrieve and extract document content for AI analysis
      let documentContents = [];
      const maxDocumentsToAnalyze = 8; // Reduced to prevent token overflow
      const documentsToAnalyze = documents?.slice(0, maxDocumentsToAnalyze);
      
      console.log(`Retrieving and extracting content from ${documentsToAnalyze?.length} documents for AI analysis...`);
      
      for (const doc of documentsToAnalyze) {
        try {
          console.log(`Processing document: ${doc?.name}`);
          const content = await this.getDocumentContent(doc?.id);
          documentContents?.push({
            name: doc?.name,
            type: doc?.type || 'Document',
            content: content?.content || `Document metadata: ${doc?.name} (${doc?.description || 'No description'})`,
            hasActualContent: content?.metadata?.hasContent || false,
            extractionMethod: content?.metadata?.extractionMethod || 'Unknown'
          });
          console.log(`Successfully processed: ${doc?.name} - Content available: ${content?.metadata?.hasContent}`);
        } catch (error) {
          console.warn(`Could not retrieve content for ${doc?.name}:`, error);
          // Still include document with metadata if content retrieval fails
          documentContents?.push({
            name: doc?.name,
            type: doc?.type || 'Document',
            content: `Document: ${doc?.name}\nType: ${doc?.type || 'Unknown'}\nDescription: ${doc?.description || 'No description available'}\nNote: Content extraction failed - ${error?.message}`,
            hasActualContent: false,
            extractionMethod: 'Error fallback'
          });
        }
      }

      // Prepare enhanced context with actual document content
      const documentContentSummary = documentContents?.map(doc => 
        `=== ${doc?.name} (${doc?.type}) ===
CONTENT STATUS: ${doc?.hasActualContent ? 'EXTRACTED' : 'METADATA ONLY'}
EXTRACTION METHOD: ${doc?.extractionMethod}

${doc?.content?.substring(0, 6000)}${doc?.content?.length > 6000 ? '\n...[CONTENT TRUNCATED]' : ''}`
      )?.join('\n\n');
      
      const analysisContext = `
ZENTRI DOCUMENT MANAGEMENT SYSTEM - AI ANALYSIS QUERY

QUERY: "${query}"

DOCUMENT ANALYSIS CONTEXT:
- Total documents in system: ${documents?.length}
- Documents analyzed for this query: ${documentContents?.length}
- Document base: ${documentBaseId === 'all' ? 'All Document Bases' : 'Selected Document Base'}
- Documents with extracted content: ${documentContents?.filter(d => d?.hasActualContent)?.length}
- Documents with metadata only: ${documentContents?.filter(d => !d?.hasActualContent)?.length}

DOCUMENT CONTENTS AND ANALYSIS:
${documentContentSummary}

ANALYSIS FINDINGS SUMMARY:
- Total findings: ${analysisData?.allFindings?.length || 0}
- Analysis categories: ${analysisData?.categories?.map(cat => `${cat?.type} (${cat?.count} issues)`)?.join(', ')}

RECENT ANALYSIS FINDINGS:
${analysisData?.allFindings?.slice(0, 3)?.map(finding => `- ${finding?.title}: ${finding?.description?.substring(0, 150)}...`)?.join('\n')}

IMPORTANT NOTES:
- Focus on the actual document content provided above
- If documents mention specific time intervals (30, 60, 90, 120, 180 days), reference them directly
- Cite specific document names when providing information
- If content extraction failed for some documents, acknowledge this limitation
      `;

      console.log('Sending enhanced context with extracted document content to OpenAI...');

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: `You are Zentri, an AI assistant specialized in document management and policy analysis. You have been provided with ACTUAL CONTENT extracted from the user's document management system.

CRITICAL INSTRUCTIONS:
- You have access to real document content, including policy documents, procedures, and guidelines
- When answering questions, reference specific sections, policies, procedures, and requirements from the provided document content
- Provide detailed answers based on the actual text content, not assumptions
- When citing information, mention the specific document name and relevant section if available
- If asked about specific policies, procedures, or requirements (like review intervals), search through the provided document content for exact information
- Be comprehensive and accurate based on the actual document content provided
- If some documents had extraction issues, acknowledge this and work with the available content
- Focus particularly on policy compliance, review schedules, access controls, and procedural requirements

DOCUMENT CONTEXT PROVIDED:
${analysisContext}

Your goal is to provide helpful, accurate responses based on the actual document content extracted from the user's document management system.`
          },
          {
            role: 'user',
            content: query
          }
        ],
        max_tokens: 1200,
        temperature: 0.2 // Lower temperature for more factual, consistent responses
      });

      const aiResponse = response?.choices?.[0]?.message?.content || 'I encountered an issue processing your query. Please try asking again.';
      
      console.log('Received enhanced response from AI with document content analysis');
      return aiResponse;

    } catch (error) {
      console.error('Error processing natural language query with enhanced document content:', error);
      
      // Enhanced error handling for specific OpenAI errors
      if (error?.status === 429) {
        return 'The AI service is currently at capacity (quota exceeded). Please try again later, or check your OpenAI account billing status. The system attempted to analyze your document content but was rate limited.';
      }
      
      if (error?.status === 401) {
        return 'AI service authentication failed. Please verify your OpenAI API key is correctly configured in the VITE_OPENAI_API_KEY environment variable.';
      }
      
      if (error?.status === 400) {
        return 'There was an issue with the AI request format. The system attempted to send enhanced document content for analysis. Please try a simpler question for now.';
      }
      
      // Enhanced fallback responses with document-specific information
      const lowercaseQuery = query?.toLowerCase();
      
      // Check if we have document content to work with
      let documents = [];
      try {
        if (documentBaseId === 'all') {
          documents = await documentService?.getAllDocuments();
        } else {
          documents = await this.getDocumentsByDocumentBase(documentBaseId);
        }
      } catch (docError) {
        console.error('Error fetching documents for fallback:', docError);
      }
      
      if (lowercaseQuery?.includes('30') || lowercaseQuery?.includes('60') || lowercaseQuery?.includes('90') || lowercaseQuery?.includes('180') || lowercaseQuery?.includes('interval')) {
        const policyDocs = documents?.filter(doc => 
          doc?.name?.toLowerCase()?.includes('policy') || 
          doc?.name?.toLowerCase()?.includes('procedure') ||
          doc?.name?.toLowerCase()?.includes('access control') ||
          doc?.name?.toLowerCase()?.includes('information protection')
        );
        
        return `I found ${policyDocs?.length} policy documents that likely contain information about periodic activities:

${policyDocs?.slice(0, 5)?.map(doc => `• ${doc?.name}`)?.join('\n')}

Based on typical organizational policies, activities at 30, 60, 90, 120, and 180-day intervals commonly include:

**30-Day Activities:**
• User access reviews for new employees
• Initial security training completion
• System access validation

**60-Day Activities:**
• Quarterly access certifications (prep)
• Security incident trend analysis
• Policy compliance spot checks

**90-Day Activities:**
• Quarterly access reviews and certifications
• Privileged access account reviews
• Security control effectiveness assessments

**120-Day Activities:**
• Mid-cycle security training
• Extended access privilege reviews
• Business process access validation

**180-Day Activities:**
• Semi-annual comprehensive access reviews
• Policy and procedure updates
• Security awareness training cycles
• Major system access recertification

**Note:** This is a general response due to AI service limitations (${error?.message || 'Service unavailable'}). For specific requirements in your organization, the AI service needs proper configuration to read actual document content from files like "${policyDocs?.[0]?.name || 'your policy documents'}".`;
      }
      
      if (lowercaseQuery?.includes('password') || lowercaseQuery?.includes('privileged')) {
        const passwordDocs = documents?.filter(doc => 
          doc?.name?.toLowerCase()?.includes('password') || 
          doc?.name?.toLowerCase()?.includes('access control') ||
          doc?.name?.toLowerCase()?.includes('security')
        );
        
        return `I found ${passwordDocs?.length} documents that likely contain password and privileged access policies:

${passwordDocs?.slice(0, 5)?.map(doc => `• ${doc?.name}`)?.join('\n')}

**Typical privileged user password requirements include:**
• More frequent password changes (30-90 days vs. standard users)
• Complex password requirements (length, character types)
• Multi-factor authentication requirements
• Account lockout and monitoring policies
• Emergency access procedures

**Note:** For specific password change frequencies and privileged user requirements in your organization, the AI service needs proper configuration to read actual document content. Error: ${error?.message || 'AI service unavailable'}`;
      }
      
      return `I encountered an error while processing your query: ${error?.message || 'Unknown error'}

I can see you have ${documents?.length} documents available in your system, but I need the AI service to be properly configured to read their content and provide detailed answers.

**Available documents include:**
${documents?.slice(0, 5)?.map(doc => `• ${doc?.name}`)?.join('\n')}
${documents?.length > 5 ? `...and ${documents?.length - 5} more documents` : ''}

**Please check:**
• OpenAI API configuration and key validity
• Network connectivity to AI services
• API quota and billing limits
• Document content extraction capabilities

Try asking a simpler question, or contact your system administrator if the issue persists.`;
    }
  },

  // Enhanced query processing for specific document analysis
  async queryDocumentContent(query, documentId) {
    try {
      if (!import.meta.env?.VITE_OPENAI_API_KEY) {
        throw new Error('OpenAI API key not configured');
      }

      const documentContent = await this.getDocumentContent(documentId);
      
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini', // Fixed: Changed from 'gpt-5-mini' to valid model
        messages: [
          {
            role: 'system',
            content: `You are analyzing a specific document for the user. The document details:
- Name: ${documentContent?.name}
- Type: ${documentContent?.mimeType}

Provide specific, accurate information about this document's content, structure, and any issues or insights you can identify.`
          },
          {
            role: 'user',
            content: `Please analyze this document and answer my question: "${query}"\n\nDocument content:\n${documentContent?.content?.substring(0, 8000)}`
          }
        ],
        max_tokens: 800, // Removed unsupported parameters
        temperature: 0.7
      });

      return response?.choices?.[0]?.message?.content;
    } catch (error) {
      console.error('Error querying document content:', error);
      
      if (error?.status === 429) {
        throw new Error('AI service quota exceeded. Please try again later or check your OpenAI billing.');
      }
      
      throw error;
    }
  },

  // Helper methods to generate realistic findings (unchanged from original)
  generateFindingTitle(doc, type) {
    const titles = {
      gap_detection: [
        `Missing key information in ${doc?.name}`,
        `Incomplete section detected in ${doc?.name}`,
        `Policy gap identified in ${doc?.name}`
      ],
      inconsistencies: [
        `Inconsistent formatting in ${doc?.name}`,
        `Variable terminology usage in ${doc?.name}`,
        `Conflicting information in ${doc?.name}`
      ],
      contradictions: [
        `Contradictory statements in ${doc?.name}`,
        `Conflicting requirements in ${doc?.name}`,
        `Opposing information found in ${doc?.name}`
      ],
      grammar_issues: [
        `Grammar errors detected in ${doc?.name}`,
        `Subject-verb disagreements in ${doc?.name}`,
        `Tense inconsistencies in ${doc?.name}`
      ],
      spelling_errors: [
        `Spelling mistakes in ${doc?.name}`,
        `Typographical errors in ${doc?.name}`,
        `Misspelled words in ${doc?.name}`
      ],
      language_issues: [
        `Unclear language in ${doc?.name}`,
        `Ambiguous terminology in ${doc?.name}`,
        `Complex sentence structure in ${doc?.name}`
      ]
    };
    
    const typeArray = titles?.[type] || [`Issue detected in ${doc?.name}`];
    return typeArray?.[Math.floor(Math.random() * typeArray?.length)];
  },

  generateFindingDescription(doc, type) {
    const descriptions = {
      gap_detection: [
        'This document appears to be missing critical information that would be expected for this document type.',
        'Key procedural steps or policy details seem to be absent from this document.',
        'Important sections that are typically required for compliance are not present.'
      ],
      inconsistencies: [
        'The document contains inconsistent formatting, terminology, or structure that may confuse readers.',
        'Different sections of the document use varying approaches to similar concepts.',
        'Inconsistent application of standards throughout the document.'
      ],
      contradictions: [
        'The document contains statements that directly contradict each other or conflict with established policies.',
        'Conflicting requirements or procedures are presented without clarification.',
        'Opposing viewpoints are presented without proper resolution.'
      ],
      grammar_issues: [
        'Multiple grammatical errors have been detected that may impact document clarity and professionalism.',
        'Subject-verb disagreements and tense inconsistencies affect readability.',
        'Sentence structure issues that could be improved for better comprehension.'
      ],
      spelling_errors: [
        'Several spelling errors have been identified that should be corrected for professional presentation.',
        'Typographical mistakes that may distract from the document content.',
        'Misspelled technical terms or proper nouns that need attention.'
      ],
      language_issues: [
        'The document contains unclear or ambiguous language that may be difficult to understand.',
        'Complex sentence structures that could be simplified for better readability.',
        'Terminology usage that may not be consistent with organizational standards.'
      ]
    };

    const typeArray = descriptions?.[type] || ['An issue has been detected in this document.'];
    return typeArray?.[Math.floor(Math.random() * typeArray?.length)];
  },

  generateLocation(doc, type) {
    const locations = [
      'Section 1.0 - Introduction',
      'Section 2.1 - Overview',
      'Section 3.0 - Procedures',
      'Section 4.2 - Guidelines',
      'Section 5.0 - Requirements',
      'Appendix A',
      'Page 2, paragraph 3',
      'Executive Summary',
      'Conclusion section'
    ];
    return locations?.[Math.floor(Math.random() * locations?.length)];
  },

  generateAffectedContent(doc, type) {
    const sampleTexts = [
      'This section outlines the basic requirements and procedures that must be followed.',
      'All employees are expected to comply with the established guidelines and protocols.',
      'The following procedures must be implemented according to organizational standards.',
      'These requirements are mandatory for all staff members and contractors.',
      'Implementation of these guidelines ensures compliance with regulatory requirements.'
    ];
    return sampleTexts?.[Math.floor(Math.random() * sampleTexts?.length)];
  },

  generateSuggestion(doc, type) {
    const suggestions = {
      gap_detection: [
        'Add the missing section with appropriate content and references.',
        'Include detailed procedures and requirements for this section.',
        'Expand the section to meet organizational standards.'
      ],
      inconsistencies: [
        'Standardize the formatting and terminology throughout the document.',
        'Apply consistent style guidelines across all sections.',
        'Align the document structure with organizational templates.'
      ],
      contradictions: [
        'Resolve the conflicting statements by clarifying the correct procedure.',
        'Update the document to eliminate contradictory information.',
        'Provide clear guidance on which requirement takes precedence.'
      ],
      grammar_issues: [
        'Correct the grammatical errors to improve document clarity.',
        'Review and fix subject-verb agreements and tense consistency.',
        'Improve sentence structure for better readability.'
      ],
      spelling_errors: [
        'Correct all spelling errors using spell-check tools.',
        'Review and fix typographical mistakes throughout the document.',
        'Verify proper spelling of technical terms and proper nouns.'
      ],
      language_issues: [
        'Simplify complex language and clarify ambiguous terms.',
        'Use more straightforward sentence structures.',
        'Apply consistent terminology throughout the document.'
      ]
    };

    const typeArray = suggestions?.[type] || ['Review and correct the identified issue.'];
    return typeArray?.[Math.floor(Math.random() * typeArray?.length)];
  },

  generateSuggestedContent(doc, type) {
    return 'This section outlines the corrected requirements and procedures that must be followed, ensuring clarity and compliance with organizational standards.';
  },

  generateAdditionalContext(doc, type) {
    const contexts = [
      'This issue affects document compliance and should be addressed promptly.',
      'Multiple similar issues have been identified in related documents.',
      'This finding is consistent with organizational quality standards.',
      'Similar patterns have been observed across the document collection.',
      'This issue may impact document usability and effectiveness.'
    ];
    return contexts?.[Math.floor(Math.random() * contexts?.length)];
  },

  // Helper method to get most common issue type
  getMostCommonIssueType(findings) {
    if (!findings || findings?.length === 0) return 'No issues found';
    
    const typeCounts = findings?.reduce((acc, finding) => {
      acc[finding?.type] = (acc?.[finding?.type] || 0) + 1;
      return acc;
    }, {});
    
    const mostCommon = Object.entries(typeCounts)?.reduce((a, b) => typeCounts?.[a] > typeCounts?.[b] ? a : b);
    
    const typeLabels = {
      gap_detection: 'Gap Detection',
      inconsistencies: 'Inconsistencies',
      contradictions: 'Contradictions',
      grammar_issues: 'Grammar Issues',
      spelling_errors: 'Spelling Errors',
      language_issues: 'Language Issues'
    };
    
    return typeLabels?.[mostCommon] || mostCommon;
  }
};